import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import CountUp from "react-countup";
import Flex from "components/common/Flex";

const StatCard = ({
  icon,
  color,
  title,
  value,
  trend,
  trendValue,
  suffix = "",
  secondaryValue,
  secondaryLabel,
  decimals = 0,
}) => {
  const isPositive = trend === "up";

  return (
    <Card
      className="h-100 border-0 rounded-4 shadow-sm transition-base"
      style={{
        border: "1px solid #edf2f9",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
      }}
    >
      <Card.Body className="">
        <Flex justifyContent="between" alignItems="start" className="">
          <div
            className="rounded-3 mb-3 d-flex align-items-center justify-content-center"
            style={{
              width: "46px",
              height: "46px",
              color: color,
              fontSize: "1.2rem",
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </div>
          {trendValue && (
            <span
              className={`badge rounded-pill px-2 py-1 fs--1 fw-bold ${isPositive ? "badge-subtle-success" : "badge-subtle-danger"}`}
            >
              {isPositive ? "+" : ""}
              {trendValue}
            </span>
          )}
        </Flex>
        <div>
          <p className="mb-2 text-500 fs--1 fw-semi-bold">{title}</p>
          <div className="d-flex align-items-baseline gap-2">
            <h3
              className="mb-0 fw-bold text-900"
              style={{ fontSize: "1.625rem", letterSpacing: "-0.02em" }}
            >
              <CountUp
                end={parseFloat(value)}
                duration={1.5}
                separator=","
                decimals={decimals}
                suffix={suffix}
              />
            </h3>
            {secondaryValue !== undefined && (
              <div className="d-flex flex-column lh-1">
                <span className="text-600 fw-bold fs--1">
                  <CountUp
                    end={parseFloat(secondaryValue)}
                    duration={1.5}
                    separator=","
                    decimals={2}
                    suffix={suffix}
                  />
                </span>
                <span
                  className="text-400 fw-semi-bold"
                  style={{ fontSize: "0.65rem" }}
                >
                  {secondaryLabel}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const DashboardStats = () => {
  const { generalDashboardData } = useSelector((state) => state.dashboard);

  // Eligibility data
  const eligibilityTotal = generalDashboardData?.eligibility?.total || 0;
  const eligibilityApproved = generalDashboardData?.eligibility?.eligibile || 0;

  const eligibilityRate =
    eligibilityTotal > 0
      ? ((eligibilityApproved / eligibilityTotal) * 100).toFixed(2)
      : 0;

  // Pre-auth data
  const preauthTotal = generalDashboardData?.preauth?.total || 0;
  const preauthApproved = generalDashboardData?.preauth?.approved || 0;
  const preauthPartial = generalDashboardData?.preauth?.partial || 0;

  const preauthRateStrict =
    preauthTotal > 0 ? ((preauthApproved / preauthTotal) * 100).toFixed(2) : 0;

  const preauthRateIncl =
    preauthTotal > 0
      ? (((preauthApproved + preauthPartial) / preauthTotal) * 100).toFixed(2)
      : 0;

  // Pre-auth response time
  const avgResponseTimeMins = generalDashboardData?.preauth?.avg_respmins || 0;
  let preauthAvgValue = avgResponseTimeMins;
  let preauthAvgSuffix = " m";
  let preauthAvgDecimals = 0;

  if (avgResponseTimeMins >= 1440) {
    preauthAvgValue = avgResponseTimeMins / 1440;
    preauthAvgSuffix = " d";
    preauthAvgDecimals = 2;
  } else if (avgResponseTimeMins >= 60) {
    preauthAvgValue = avgResponseTimeMins / 60;
    preauthAvgSuffix = " h";
    preauthAvgDecimals = 2;
  }

  const stats = [
    {
      title: "Total Eligibility",
      value: eligibilityTotal,
      icon: "users",
      color: "#2b92ed",
      suffix: "",
    },
    {
      title: "Successful Eligibility Rate",
      value: eligibilityRate,
      icon: "check-circle",
      color: "#2b92ed",
      suffix: "%",
      decimals: 1,
    },
    {
      title: "Pre-Auth Submissions",
      value: preauthTotal,
      icon: "tasks",
      color: "#8a3cf6",
      suffix: "",
    },
    {
      title: "Pre-Auth Approval Rate",
      value: preauthRateStrict,
      secondaryValue: preauthRateIncl,
      secondaryLabel: "INCL. PARTIAL",
      icon: "percentage",
      color: "#00d27a",
      suffix: "%",
      decimals: 1,
    },
    {
      title: "Avg. Pre-auth Response Time",
      value: preauthAvgValue,
      icon: "clock",
      color: "#f5803e",
      suffix: preauthAvgSuffix,
      decimals: preauthAvgDecimals,
    },
  ];

  return (
    <Row className="g-3 mb-3">
      {stats.map((stat, index) => (
        <Col key={index} xs={12} sm={6} md={4} xl className="col-xl">
          <StatCard {...stat} />
        </Col>
      ))}
    </Row>
  );
};

export default DashboardStats;
